import { Spin } from "antd";
import EmailVerificationBadge from "components/atoms/EmailVerificationBadge";
import dayjs from "dayjs";
import React from "react";
import API, { Endpoints } from "services/api";

interface IDataObjectFieldsPreviewProps {
    tenantId: string;
    businessAreaId: string;
    schemaId: string;
    dataSetId: string;
    dataObjectId: string;
}

export default function DataObjectFieldsPreview({ dataObjectId, schemaId, businessAreaId, dataSetId, tenantId }: IDataObjectFieldsPreviewProps) {
    const [isLoadingDataObject, setIsLoadingDataObject] = React.useState<boolean>(true);
    const [dataObject, setDataObject] = React.useState<Record<string, string> | null>(null);
    const [dataObjectLoadError, setDataObjectLoadError] = React.useState<string | null>(null);
    const [isLoadingSchemaModel, setIsLoadingSchemaModel] = React.useState<boolean>(true);
    const [schemaModel, setSchemaModel] = React.useState<ISchemaModel | null>(null);
    const [schemaLoadError, setSchemaLoadError] = React.useState<string | null>(null);

    React.useEffect(() => {
        setIsLoadingDataObject(true);
        const api = new API();
        api.get(`${Endpoints.datasets}/api/v1/businessareas/${businessAreaId}/schemas/${schemaId}/datasets/${dataSetId}/dataobjects/${dataObjectId}`, { tenantid: tenantId })
            .then((response) => setDataObject(response.data))
            .catch((_e) => setDataObjectLoadError("Unable to load data object."))
            .finally(() => setIsLoadingDataObject(false));

        return () => setIsLoadingDataObject(true);
    }, [businessAreaId, dataObjectId, dataSetId, schemaId, tenantId]);

    React.useEffect(() => {
        setIsLoadingSchemaModel(true);
        const api = new API();
        api.get(`${Endpoints.schemas}/api/v1/businessareas/${businessAreaId}/schemas/${schemaId}`, { tenantid: tenantId })
            .then((response) => setSchemaModel(response.data))
            .catch((_e) => setSchemaLoadError("Unable to load schema model."))
            .finally(() => setIsLoadingSchemaModel(false));

        return () => setIsLoadingSchemaModel(true);
    }, [businessAreaId, schemaId, tenantId]);

    const getObject = React.useCallback(
        (modelFieldId: string) => {
            if (!!dataObject && Object.keys(dataObject).some((fieldId) => fieldId === modelFieldId)) {
                try {
                    const dataObjectField = dataObject[modelFieldId];
                    return dataObjectField;
                } catch (e) {
                    return "";
                }
            }
            return "";
        },
        [dataObject],
    );

    return (
        <div key={dataObjectId} className="flex flex-col gap-2">
            {!!dataObject && !!schemaModel && (
                <div className="grid grid-cols-[max-content_1fr] gap-x-2 border border-yellow-100 bg-yellow-50">
                    {getObject("createdDate") && (
                        <>
                            <div className="p-1 font-bold">Created</div>
                            <div className="p-1">{dayjs(getObject("createdDate")).format("DD MMM YYYY HH:mm:ss")}</div>
                        </>
                    )}
                    {getObject("updatedDate") && (
                        <>
                            <div className="p-1 font-bold">Last Updated</div>
                            <div className="p-1">{dayjs(getObject("updatedDate")).format("DD MMM YYYY HH:mm:ss")}</div>
                        </>
                    )}
                </div>
            )}
            {isLoadingSchemaModel || isLoadingDataObject ? (
                <div className="flex gap-1">
                    <Spin size="small" />
                    Loading...
                </div>
            ) : (
                <table className="w-full table-auto">
                    <tbody>
                        {schemaModel?.fields.map((modelField) => (
                            <tr key={modelField.fieldId} className="even:bg-slate-50">
                                <td className="p-1 font-bold">{modelField.name}</td>
                                <td className="p-1">
                                    {getObject(modelField.fieldId)}
                                    {modelField.dataType === "email" && (dataObject as any)?.verificationResults?.[modelField.fieldId] && (
                                        <EmailVerificationBadge verificationResult={(dataObject as any).verificationResults[modelField.fieldId]} />
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
            {dataObjectLoadError && <div className="rounded-md bg-red-50 p-2 text-red-500">{dataObjectLoadError}</div>}
            {schemaLoadError && <div className="rounded-md bg-red-50 p-2 text-red-500">{schemaLoadError}</div>}
        </div>
    );
}
